// @ds/config/presentation — the hand-authored presentation layer (Layer 3 of
// docs/plans/schema-codegen-architecture.md).
//
// `definePresentation(table, fields)` takes keyed presentation fields whose DB
// bindings are `db.*` objects (never strings) and:
//   - injects each field's `key` from its property name,
//   - derives a default `labelKey` (`<table>.fields.<key>`),
//   - resolves relation embed aliases (`company_id` FK → `company` embed),
//   - fills column-backed fields that omit `component`/`edit`/`filterable` from
//     the generated default for that column's type/role (`defaultField`).
//
// `deriveSelect(schema)` walks each field's binding OBJECTS and computes the
// PostgREST select string. `fieldDeps(field)` reports the columns/relations a
// single field touches. Nothing structural is ever hand-written as a string.

import type { ComponentType } from "react";
import type { FilterDef } from "@ds/tables/types";
import type { EditFieldSpec } from "@ds/editable/editFieldSpec";
import type { AnyColumnRef, TableName, ColumnOf } from "./db/graph";
import { COLUMN_META, PG_ENUMS, type ColumnMeta } from "./db/columnMeta.generated";
import { columnEnum, type ColumnEnum } from "./db/columnEnums";
import { StatusChipShell } from "@ds/display/StatusChipShell";
import { DateValue } from "@ds/display/DateValue";
import { TextValue } from "@ds/display/TextValue";
import { NumberValue } from "@ds/display/NumberValue";
import { EnumChips } from "@ds/display/EnumChips";
import { CopyableValue, type CopyableFormat } from "@ds/display/CopyableValue";
import { EntityLink } from "@ds/connected/EntityLink";
import type { EntityType } from "@ds/navigation/navigation";
import type { ToneName } from "@ds/tokens/palette";
import {
  selectFilter,
  dateRangeFilter,
  textFilter,
  entitySelectFilter,
} from "@ds/tables/helpers/sharedFilterDefs";

// ─── Relation binding ──────────────────────────────────────────────────────
//
// A FK relation a field embeds: the FK column on the source table + the columns
// to project on the target table. The embed alias is derived from the FK column
// name (`company_id` → `company`) so it never collides with the scalar FK.

export interface RelationBinding {
  /** FK column on the source table (`db.<source>.<fk>`). */
  fk: AnyColumnRef;
  /** Columns to project on the target table (`db.<target>.*`). */
  targetCols: AnyColumnRef[];
  /**
   * Override the embed alias. Defaults to `relationAlias(fk.column)`
   * (`company_id` → `company`). Set this when the natural alias collides
   * with a scalar column on the parent (e.g. `edpr_requests.applicant_company`
   * text vs `applicant_company_id` FK → `alias: "applicant_company_ref"`),
   * or when the consumer cell reads from a non-natural embed name
   * (`rec.edpr_requests.*` rather than `rec.edpr_request.*`).
   *
   * Prefer this over the legacy field-level `__alias` post-spread: setting
   * it here keeps the binding, the deriver, the deps and the `data` lookup
   * all aligned on the same alias.
   */
  alias?: string;
  /**
   * Primary-key column of the TARGET table, seeded into the embed's field
   * list. Defaults to `id`. Set it for targets whose pk is not `id`
   * (e.g. `fakturownia_payment`, pk `external_payment_id`) — seeding a
   * non-existent `id` makes PostgREST reject the whole query with a 400.
   */
  pk?: string;
}

// ─── PresentationField ─────────────────────────────────────────────────────

export interface PresentationFieldEdit {
  /** Scalar column written by the edit widget. */
  key: string;
  field?: EditFieldSpec;
  required?: boolean;
  readOnly?: boolean;
  validate?: (v: unknown) => string | null;
}

export interface PresentationField<P = unknown> {
  /** Injected by `definePresentation` from the property name. */
  key?: string;
  /** Embed alias for the field's relation (derived from the FK column). */
  __alias?: string;

  // ── DB bindings (all `db.*` objects, never strings) ──
  /** Primary backing scalar column. */
  column?: AnyColumnRef;
  /** Extra scalar columns the `data` accessor reads. */
  cols?: AnyColumnRef[];
  /** A single FK relation this field embeds (from a relation spec). */
  relation?: RelationBinding;
  /** Multiple FK relations (synthetic composite, e.g. a load+unload route). */
  relations?: RelationBinding[];

  // ── presentation ──
  component?: ComponentType<P>;
  props?: Partial<P>;
  data?: (record: Record<string, unknown>) => Partial<P>;
  labelKey?: string;
  sortable?: boolean;
  /** A FilterDef, or `false` to opt the field OUT of the auto-derived filter
   *  set (suppresses the default filter `defaultField` would otherwise add). */
  filterable?: FilterDef | false;
  edit?: PresentationFieldEdit;
  /**
   * Render the column's value with a copy affordance. `true` → plain copyable;
   * a `CopyableFormat` ("nip" | "iban" | "email" | …) → copyable with that mask.
   * A flag-only override — no need to restate `component`/`data`/`column`.
   */
  copyable?: boolean | CopyableFormat;
  /**
   * Render the column's value as an EntityLink to that entity type's detail
   * (label = the column value, id = record.id). For a COMPOSED label, author a
   * full `component`/`data` override instead. Flag-only — no restating column.
   */
  link?: EntityType;
}

export type PresentationSchema = {
  readonly [key: string]: PresentationField<unknown>;
} & { readonly __table?: TableName };

/**
 * A map of heterogeneously-typed presentation fields. The `any` is irreducible:
 * each field carries its own component prop type `P`, and TS has no existential
 * to quantify those in one object type. Centralized here so authored schemas and
 * generated `*.fields.generated.ts` files reference it instead of repeating the
 * `satisfies Record<string, PresentationField<any>>` + eslint-disable per file.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyPresentationField = PresentationField<any>;

export type PresentationFieldMap = Record<string, AnyPresentationField>;

// ─── Alias derivation ──────────────────────────────────────────────────────

/** `company_id` → `company`; `load_place_company_id` → `load_place_company`. */
export function relationAlias(fkColumn: string): string {
  return fkColumn.endsWith("_id") ? fkColumn.slice(0, -3) : fkColumn;
}

/** Columns whose label lives in the shared `common.fields.*` namespace, not
 *  per-entity. Keeps audit-field labels translated once, app-wide. */
const COMMON_LABEL_KEYS: Record<string, string> = {
  created_at: "common.fields.created_at",
  updated_at: "common.fields.updated_at",
};

/**
 * Per-table label namespace overrides. Some entities have one shared locale
 * namespace covering several Postgres tables (e.g. all three `edpr_*` tables
 * share the `edpr` namespace; `transactions_with_checks` reads from
 * `transactions`). Keep this map small — only add entries when the natural
 * `<table>.fields.<column>` lookup misses.
 */
const TABLE_LABEL_NAMESPACE: Record<string, string> = {
  // All three `edpr_*` tables share the flat `edpr.fields.*` namespace —
  // matches how the locale file is organised (one `fields` block covers
  // request + document + record columns). Request-specific overrides live
  // under `edpr.request.fields.*` but the auto-derived default falls back
  // to the shared namespace so missing per-request keys aren't a crash.
  edpr_documents:           "edpr",
  edpr_records:             "edpr",
  edpr_requests:            "edpr",
  transactions_with_checks: "transactions",
  // commission_* tables share the flat `commission.fields.*` namespace
  // (one locale file covers rules + payees + EDPR rate columns).
  commission_rules:         "commission",
  commission_payees:        "commission",
  edpr_rates:               "commission",
  // All `mbank_*` tables share the flat `bank.fields.*` namespace (one locale
  // file covers batches / payments / statements / lines / links — shared
  // column names get one header label).
  mbank_outbound_batch:     "bank",
  mbank_outbound_payment:   "bank",
  mbank_statement:          "bank",
  mbank_statement_line:     "bank",
  mbank_payment_links:      "bank",
};

/** i18n key for a column's label: shared `common.fields.*` for audit columns,
 *  else `<namespace>.fields.<column>` where namespace defaults to the table
 *  name unless overridden in `TABLE_LABEL_NAMESPACE`. */
export function fieldLabelKey(table: string, column: string): string {
  if (COMMON_LABEL_KEYS[column]) return COMMON_LABEL_KEYS[column];
  const ns = TABLE_LABEL_NAMESPACE[table] ?? table;
  return `${ns}.fields.${column}`;
}

// ─── defaultField — the generated default for one column ────────────────────
//
// Reads RUNTIME column metadata (typegen is compile-time only and erases
// timestamp-vs-text). Produces a sensible component-less `edit`/`filterable`
// default keyed on the column's Postgres type, FK-ness, and enum membership.
// Authored fields override any of these (shallow merge in `definePresentation`).

const DATE_TYPES = new Set(["date", "timestamp without time zone", "timestamp with time zone"]);
const NUMERIC_TYPES = new Set(["numeric", "double precision", "real"]);
const INT_TYPES = new Set(["integer", "bigint", "smallint"]);

interface DefaultFieldResult {
  edit?: PresentationFieldEdit;
  filterable?: FilterDef;
  /** Scalar columns are sortable by default. */
  sortable?: boolean;
  /** Set when the column is an app-enum (text column treated as enum). */
  enum?: ColumnEnum;
  /** Generic display the generator wires when the author supplies no component:
   *  enum/boolean → StatusChipShell, date → DateValue. */
  display?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: ComponentType<any>;
    data: (r: Record<string, unknown>) => Record<string, unknown>;
  };
}

/** StatusChipShell data: tone + label, or em-dash on null. */
function chipData(
  tones: Record<string, ToneName>,
  labels: Record<string, string>,
  column: string,
) {
  return (r: Record<string, unknown>) => {
    const v = r[column] as string | null | undefined;
    return v == null
      ? { tone: null, label: null }
      : { tone: tones[v] ?? "grey", label: labels[v] ?? v };
  };
}

/**
 * The default `edit` + `filterable` for `<table>.<column>`, derived from the
 * column's app-enum binding (`columnEnums.ts`) first, then its runtime type /
 * DB-enum membership. Returns `{}` for unknown columns.
 */
/** PK / system columns that should never get an auto-derived list filter. */
const NON_FILTERABLE_COLUMNS = new Set(["id", "is_deleted", "created_by", "updated_by"]);

/**
 * True when a column should NOT auto-generate a filter: PK/system columns, and
 * plain numbers (exact-match text filtering on a number is useless — no range).
 * Real entity FKs (company / product / employee ids) keep their entity-select
 * filter; bare int FKs to non-entity tables (price_list_id, rule_id, …) don't.
 */
function isNonFilterableColumn(table: string, column: string): boolean {
  if (NON_FILTERABLE_COLUMNS.has(column)) return true;
  const meta = COLUMN_META[table]?.[column];
  if (!meta) return false;
  if (meta.fk && entityKindForTable(meta.fk)) return false;
  return NUMERIC_TYPES.has(meta.dt) || INT_TYPES.has(meta.dt);
}

export function defaultField(table: string, column: string): DefaultFieldResult {
  const res = defaultFieldRaw(table, column);
  if (res.filterable && isNonFilterableColumn(table, column)) {
    const { filterable: _omit, ...rest } = res;
    return rest;
  }
  return res;
}

function defaultFieldRaw(table: string, column: string): DefaultFieldResult {
  const meta: ColumnMeta | undefined = COLUMN_META[table]?.[column];
  const nullable = meta ? meta.n : true;
  const required = meta ? !meta.n && !meta.d : false; // NOT NULL and no default
  const editBase = { key: column, ...(required ? { required: true } : {}) };

  // EntityListPage's translateFilters resolves a FilterDef's `label` via
  // t(label) — so every generated filter uses the i18n key AS its label (it
  // falls back to the key string when missing). Every column is filterable.
  const labelKey = fieldLabelKey(table, column);
  const optsOf = (values: readonly string[], labels?: Record<string, string>) =>
    values.map((v) => ({ label: labels?.[v] ?? v, value: v }));

  // App-enum (text column whose value set lives in src/types/enums.ts).
  const appEnum = columnEnum(table, column);
  if (appEnum) {
    const isArray = meta?.dt === "ARRAY";
    const options = optsOf(appEnum.values, appEnum.labels);
    return {
      edit: {
        ...editBase,
        field: isArray
          ? { type: "multiselect", options }
          : { type: "select", options, allowClear: nullable },
      },
      filterable: selectFilter({ name: column, label: labelKey, options, multiple: true, arrayColumn: isArray }),
      // arrays aren't meaningfully sortable
      sortable: !isArray,
      enum: appEnum,
      display: isArray
        ? {
            component: EnumChips,
            data: (r) => ({ values: r[column], tones: appEnum.tones, labels: appEnum.labels }),
          }
        : { component: StatusChipShell, data: chipData(appEnum.tones, appEnum.labels, column) },
    };
  }

  if (!meta) return {};

  // DB-native enum columns → select widget + multi-select filter from PG_ENUMS.
  const enumValues = PG_ENUMS[meta.udt];
  if (enumValues) {
    return {
      edit: { ...editBase, field: { type: "select", options: optsOf(enumValues), allowClear: meta.n } },
      filterable: selectFilter({ name: column, label: labelKey, options: optsOf(enumValues), multiple: true }),
      sortable: true,
      display: { component: StatusChipShell, data: chipData({}, {}, column) },
    };
  }

  // FK columns → entity-select edit + entity-select (multi) filter.
  if (meta.fk) {
    const kind = entityKindForTable(meta.fk);
    if (kind) {
      return {
        edit: { ...editBase, field: { type: "entity-select", kind } },
        filterable: entitySelectFilter({ name: column, label: labelKey, kind, multiple: true }),
        sortable: true,
      };
    }
  }

  const dt = meta.dt;
  if (dt === "boolean") {
    return {
      sortable: true,
      edit: { ...editBase, field: { type: "boolean" } },
      filterable: selectFilter({
        name: column,
        label: labelKey,
        options: [{ label: "Tak", value: "true" }, { label: "Nie", value: "false" }],
        multiple: true,
      }),
      display: {
        component: StatusChipShell,
        data: (r) => {
          const v = r[column];
          return v == null
            ? { tone: null, label: null }
            : { tone: v ? "green" : "grey", label: v ? "Tak" : "Nie" };
        },
      },
    };
  }
  if (DATE_TYPES.has(dt)) {
    const variant = dt === "date" ? "date" : "timestamp";
    return {
      sortable: true,
      edit: { ...editBase, field: { type: "date" } },
      filterable: dateRangeFilter(column, labelKey),
      display: { component: DateValue, data: (r) => ({ value: r[column], variant }) },
    };
  }
  if (NUMERIC_TYPES.has(dt)) {
    return {
      edit: { ...editBase, field: { type: "number", precision: 2 } },
      sortable: true,
      filterable: textFilter(column, labelKey),
      display: { component: NumberValue, data: (r) => ({ value: r[column], precision: 2 }) },
    };
  }
  if (INT_TYPES.has(dt)) {
    return {
      edit: { ...editBase, field: { type: "number" } },
      sortable: true,
      filterable: textFilter(column, labelKey),
      display: { component: NumberValue, data: (r) => ({ value: r[column], precision: 0 }) },
    };
  }
  if (dt === "jsonb" || dt === "json") {
    // jsonb ordering is type-order gibberish and `ilike` on jsonb 400s the
    // whole PostgREST query — neither sortable nor filterable. Display
    // stringifies compactly (strings render bare); edits go through the
    // `json` widget (raw-string emit, parsed once at submit).
    return {
      sortable: false,
      edit: { ...editBase, field: { type: "json" } },
      display: {
        component: TextValue,
        data: (r) => {
          const v = r[column];
          return { value: v == null ? null : typeof v === "string" ? v : JSON.stringify(v) };
        },
      },
    };
  }
  // text / varchar / everything else
  return {
    sortable: true,
    edit: { ...editBase, field: { type: "text" } },
    filterable: textFilter(column, labelKey),
    display: { component: TextValue, data: (r) => ({ value: r[column] }) },
  };
}

/** Map a target table to the matching `entity-select` kind, when one exists. */
function entityKindForTable(
  table: string,
): import("@ds/connected/EntitySelect").EntityKind | undefined {
  switch (table) {
    case "companies":
      return "company";
    case "products":
      return "product";
    case "employees":
      return "employee";
    case "transactions":
      return "transaction";
    default:
      return undefined;
  }
}

// ─── definePresentation ────────────────────────────────────────────────────

/**
 * Build a presentation schema for `table`. The full column set is included
 * AUTOMATICALLY from `COLUMN_META` (each as a column-backed field resolved by
 * `defaultField` — widget/display/enum/filter/sortable). `overrides` supply only
 * what the generator can't derive: composed labels, composite cells, relation
 * specs (with context), and synthetic fields (a key not backed by a column).
 *
 * No generated file — the column list is read from `COLUMN_META` at call time.
 * `__table` is attached non-enumerably for the derivers.
 */
export function definePresentation<T extends TableName, O extends PresentationFieldMap>(
  table: T,
  overrides: O = {} as O,
): DefinedPresentation<T, O> {
  // Auto-include every column (skip generated/tsvector).
  const base: Record<string, PresentationField<unknown>> = {};
  const cols = COLUMN_META[table] ?? {};
  for (const col of Object.keys(cols)) {
    if (cols[col]?.dt === "tsvector") continue;
    // Same shape the `db` proxy synthesizes — built directly because `col` is a
    // runtime string and can't index the typed graph.
    base[col] = { column: { table, column: col } };
  }

  // Merge overrides onto the auto-included columns. A FULL override (one that
  // re-binds the column itself — has `column`/`cols`/`relation`/`component`)
  // REPLACES the auto field. A FLAG-only override (`copyable`/`sortable`/`edit`/
  // `filterable`/`props`/`labelKey`) MERGES onto the auto column so authors can
  // tweak one aspect without restating `column`.
  const fields: Record<string, PresentationField<unknown>> = { ...base };
  for (const key of Object.keys(overrides)) {
    const ov = overrides[key] as PresentationField<unknown>;
    const auto = base[key];
    const isFull = !!(ov.column || ov.cols || ov.relation || ov.relations || ov.component);
    fields[key] = auto && !isFull ? { ...auto, ...ov } : ov;
  }

  const out: Record<string, PresentationField<unknown>> = {};

  for (const key in fields) {
    const authored = fields[key];
    const field: PresentationField<unknown> = { ...authored, key };

    // Resolve relation embed alias: explicit binding `alias` first, then
    // legacy `__alias`, then derived from the FK column.
    if (field.relation && !field.__alias) {
      field.__alias = field.relation.alias ?? relationAlias(field.relation.fk.column);
    }

    // Default labelKey: `<table>.fields.<key>` — but relation fields use the
    // embed alias (`company`, not `company_id`) to match the locale keys, and
    // audit columns use the shared `common.fields.*` namespace.
    if (!field.labelKey) {
      const labelName = field.relation ? (field.__alias ?? key) : key;
      field.labelKey = fieldLabelKey(table, labelName);
    }

    // Fill column-backed defaults the author didn't supply (shallow merge;
    // `edit`/`filterable` only filled when wholly absent).
    if (field.column) {
      const colName = field.column.column;
      const def = defaultField(table, colName);
      if (!field.edit && def.edit) field.edit = def.edit;
      // `=== undefined` (not `!field.filterable`) so an explicit `false`
      // opt-out survives instead of being refilled with the default.
      if (field.filterable === undefined && def.filterable) field.filterable = def.filterable;
      if (field.sortable === undefined && def.sortable) field.sortable = def.sortable;
      // Display (author `component` wins): `link` flag → EntityLink to that
      // entity's detail; `copyable` flag → CopyableValue (optional mask); else
      // the generic default (enum/boolean → StatusChipShell, date → DateValue,
      // number → NumberValue, text → TextValue).
      if (!field.component) {
        if (field.link) {
          const type = field.link;
          field.component = EntityLink as ComponentType<unknown>;
          field.props = { type, variant: "inline", ...(field.props as object) };
          field.data = (r) => ({ id: r.id, label: r[colName] });
        } else if (field.copyable) {
          const format = field.copyable === true ? "plain" : field.copyable;
          field.component = CopyableValue as ComponentType<unknown>;
          field.data = (r) => ({ value: r[colName], format });
        } else if (def.display) {
          field.component = def.display.component;
          field.data = def.display.data;
        }
      }
    }

    out[key] = field;
  }

  Object.defineProperty(out, "__table", {
    value: table,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  return out as unknown as DefinedPresentation<T, O>;
}

/** The keyed result of `definePresentation`: every column of `T` plus every
 *  override key, each with its `key` injected. */
type DefinedPresentation<T extends TableName, O> = PresentationSchema & {
  readonly [K in (ColumnOf<T> | keyof O) & string]: AnyPresentationField & { key: K };
};

// ─── Derivation — select string + per-field deps ───────────────────────────

function fieldRelations(field: PresentationField): RelationBinding[] {
  const out: RelationBinding[] = [];
  if (field.relation) out.push(field.relation);
  if (field.relations) out.push(...field.relations);
  return out;
}

/**
 * The scalar columns + relation embeds a single field touches. `scalars` are
 * column names on the root table; `embeds` are PostgREST embed clauses.
 */
export function fieldDeps(field: AnyPresentationField): {
  scalars: string[];
  embeds: string[];
} {
  const scalars = new Set<string>();
  if (field.column) scalars.add(field.column.column);
  field.cols?.forEach((c) => scalars.add(c.column));

  const embeds: string[] = [];
  for (const rel of fieldRelations(field)) {
    scalars.add(rel.fk.column); // scalar FK travels alongside the embed
    const alias = rel.alias ?? relationAlias(rel.fk.column);
    const cols = rel.targetCols.map((c) => c.column).join(",");
    embeds.push(`${alias}:${rel.fk.column}(${cols})`);
  }
  return { scalars: [...scalars], embeds };
}

/**
 * Compute the PostgREST `select` string for a whole presentation schema by
 * walking each field's binding OBJECTS. Always includes `id`. Order: scalar
 * columns first (id leading), then relation embeds.
 */
export function deriveSelect(schema: PresentationSchema): string {
  const scalars = new Set<string>(["id"]);
  // Group embeds by alias so two fields on the same FK (e.g. a chip + a card)
  // produce ONE embed with the UNION of their target columns — never a
  // duplicate alias (which PostgREST rejects).
  const embeds = new Map<string, { fk: string; cols: Set<string> }>();

  for (const key of Object.keys(schema)) {
    const field = schema[key];
    if (!field) continue;
    if (field.column) scalars.add(field.column.column);
    field.cols?.forEach((c) => scalars.add(c.column));
    for (const rel of fieldRelations(field)) {
      scalars.add(rel.fk.column);
      const alias = rel.alias ?? relationAlias(rel.fk.column);
      const e = embeds.get(alias) ?? { fk: rel.fk.column, cols: new Set<string>() };
      rel.targetCols.forEach((c) => e.cols.add(c.column));
      embeds.set(alias, e);
    }
  }

  const embedStrs = [...embeds].map(([alias, e]) => `${alias}:${e.fk}(${[...e.cols].join(",")})`);
  return [...scalars, ...embedStrs].join(",");
}
